import React, { useMemo } from 'react';
import { Box, Typography, Chip, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MappingDiagram from './MappingDiagram';
import {
  parseAnswer,
  getConnectionStatuses,
  gradeMapping,
  defaultMappingConfig,
} from './mappingUtils';

/**
 * Read-only component for displaying submitted mapping answers
 * Shows student's connections with correct/incorrect highlighting
 */
const MappingReadOnly = ({
  config = defaultMappingConfig,
  studentAnswer = '',
  correctAnswer = '',
  showCorrectAnswer = true,
  showGradeBadge = true,
}) => {
  // Parse answers
  const studentConnections = useMemo(
    () => parseAnswer(studentAnswer).connections,
    [studentAnswer]
  );

  const correctConnections = useMemo(
    () => parseAnswer(correctAnswer).connections,
    [correctAnswer]
  );

  // Get connection statuses for visual display
  const connectionStatuses = useMemo(
    () => getConnectionStatuses(studentConnections, correctConnections),
    [studentConnections, correctConnections]
  );

  // Grade the answer
  const gradeResult = useMemo(
    () => gradeMapping(studentAnswer, correctAnswer, config),
    [studentAnswer, correctAnswer, config]
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* Grade result */}
      {showGradeBadge && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          {gradeResult.isCorrect ? (
            <Chip
              icon={<CheckCircleIcon />}
              label="Correct"
              color="success"
              variant="filled"
            />
          ) : (
            <Chip
              icon={<CancelIcon />}
              label="Incorrect"
              color="error"
              variant="filled"
            />
          )}

          {config.grading?.mode === 'partial' && !gradeResult.isCorrect && (
            <Typography variant="body2" color="text.secondary">
              Score: {Math.round(gradeResult.score * 100)}%
            </Typography>
          )}
        </Box>
      )}

      {/* Mapping diagram with statuses */}
      <MappingDiagram
        leftItems={config.leftItems || []}
        rightItems={config.rightItems || []}
        connections={studentConnections}
        connectionStatuses={showCorrectAnswer ? connectionStatuses : null}
        readOnly={true}
        config={config}
      />

      {/* Legend */}
      {showCorrectAnswer && (
        <Box sx={{ mt: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 24,
                height: 3,
                backgroundColor: 'success.main',
                borderRadius: 1,
              }}
            />
            <Typography variant="caption">Correct connection</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 24,
                height: 3,
                backgroundColor: 'error.main',
                borderRadius: 1,
              }}
            />
            <Typography variant="caption">Incorrect connection</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 24,
                height: 3,
                backgroundColor: 'info.main',
                borderRadius: 1,
                borderStyle: 'dashed',
                borderWidth: 1,
                borderColor: 'info.main',
              }}
            />
            <Typography variant="caption">Missing connection</Typography>
          </Box>
        </Box>
      )}

      {/* Detailed feedback */}
      {showCorrectAnswer && !gradeResult.isCorrect && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Correct answer:</strong>
          </Typography>
          <Box sx={{ mt: 1 }}>
            {correctConnections.map((conn, i) => {
              const leftItem = config.leftItems.find((item) => item.id === conn.from);
              const rightItem = config.rightItems.find((item) => item.id === conn.to);
              return (
                <Typography key={i} variant="body2">
                  {leftItem?.value || 'Item'} → {rightItem?.value || 'Item'}
                </Typography>
              );
            })}
          </Box>
        </Alert>
      )}
    </Box>
  );
};

export default MappingReadOnly;
